import { useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { geometryService } from '../../api/client';
import { Field, FieldRow, MessageList, SectionCard } from '../../shared/components';
import type {
  BuildUpLayer,
  RoofModule,
  SectionProfile,
  SteelDeckProfileGeometry,
  SubstrateCategory,
} from '../../gen/ejot/roofing/v1/types';

interface SectionProfileProps {
  module: RoofModule | undefined;
  onModuleChange?: (module: RoofModule) => void;
}

const SUBSTRATES: { value: SubstrateCategory; label: string }[] = [
  { value: 'SUBSTRATE_CATEGORY_STEEL_DECK', label: 'Steel deck' },
  { value: 'SUBSTRATE_CATEGORY_CONCRETE', label: 'Concrete' },
  { value: 'SUBSTRATE_CATEGORY_TIMBER', label: 'Timber' },
  { value: 'SUBSTRATE_CATEGORY_BOARD', label: 'Board' },
  { value: 'SUBSTRATE_CATEGORY_CUSTOM', label: 'Custom' },
];

const SUBSTRATE_DEFAULT_THICKNESS_MM: Partial<Record<SubstrateCategory, number>> = {
  SUBSTRATE_CATEGORY_STEEL_DECK: 0.7,
  SUBSTRATE_CATEGORY_CONCRETE: 200,
  SUBSTRATE_CATEGORY_TIMBER: 22,
};

function useValidatedSection(module: RoofModule | undefined) {
  const section = useMemo<SectionProfile>(() => normalizeSection(module?.build_up?.section), [module?.build_up?.section]);
  const validation = useMutation({
    mutationFn: (current: SectionProfile) => geometryService.validateSectionProfile({ section: current }),
  });

  useEffect(() => {
    validation.mutate(section);
    // The mutation function identity is stable for the component lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  return { section, validation };
}

/**
 * Roof-owned Section/Profile inputs.
 *
 * This component edits only the selected RoofModule. The centre workspace uses
 * ProfessionalSectionEditor below only as visual verification, so Roof 1 and
 * Roof 2 can carry completely different build-up/profile inputs without a
 * second editor state.
 */
export function SectionProfileInputs({ module, onModuleChange }: SectionProfileProps) {
  const { section, validation } = useValidatedSection(module);
  if (!module || !onModuleChange) return <div className="empty-state">Select a roof to edit its section.</div>;

  const updateSection = (next: SectionProfile) => {
    onModuleChange({
      ...module,
      build_up: { ...module.build_up, section: next },
    });
  };
  const setLength = (key: keyof SectionProfile, millimetres: number) => {
    updateSection({ ...section, [key]: { millimetres: finiteOrZero(millimetres), origin: 'VALUE_ORIGIN_INPUT' } });
  };
  const setDeck = (key: keyof SteelDeckProfileGeometry, value: unknown) => {
    updateSection({ ...section, deck_profile: { ...section.deck_profile, [key]: value } });
  };
  const layers = section.layers ?? [];

  const changeSubstrateCategory = (category: SubstrateCategory) => {
    const defaultThickness = SUBSTRATE_DEFAULT_THICKNESS_MM[category];
    const currentThickness = section.substrate_thickness?.millimetres;
    updateSection({
      ...section,
      substrate_category: category,
      substrate_thickness: defaultThickness !== undefined
        ? { millimetres: defaultThickness, origin: 'VALUE_ORIGIN_INPUT' }
        : currentThickness !== undefined && currentThickness > 0
          ? section.substrate_thickness
          : undefined,
    });
  };

  return (
    <div className="section-profile-inputs">
      <SectionCard title="Insulation and anchorage" defaultOpen>
        <FieldRow>
          <Field label="Base insulation" unit="mm" type="number"
            value={numberInputValue(section.base_insulation_thickness?.millimetres)}
            onChange={(value) => setLength('base_insulation_thickness', Number(value))} />
          <Field label="Additional insulation" unit="mm" type="number"
            value={numberInputValue(section.additional_insulation_thickness?.millimetres)}
            onChange={(value) => setLength('additional_insulation_thickness', Number(value))} />
        </FieldRow>
        <FieldRow>
          <Field label="Existing layers" unit="mm" type="number"
            value={numberInputValue(section.existing_layer_thickness?.millimetres)}
            onChange={(value) => setLength('existing_layer_thickness', Number(value))} />
          <Field label="Anchorage depth" unit="mm" type="number"
            value={numberInputValue(section.anchorage_depth?.millimetres)}
            onChange={(value) => setLength('anchorage_depth', Number(value))} />
        </FieldRow>
        <Field label="Required fastener length" unit="mm" derived
          value={wholeMillimetres(validation.data?.section?.total_fixing_length?.millimetres)} />
      </SectionCard>

      <SectionCard title="Substrate" defaultOpen>
        <FieldRow>
          <label className="field">
            <span className="field-label">Substrate category</span>
            <select
              className="field-input"
              value={section.substrate_category ?? 'SUBSTRATE_CATEGORY_STEEL_DECK'}
              onChange={(event) => changeSubstrateCategory(event.target.value as SubstrateCategory)}
            >
              {SUBSTRATES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <Field label="Substrate thickness" unit="mm" type="number"
            value={numberInputValue(section.substrate_thickness?.millimetres)}
            onChange={(value) => setLength('substrate_thickness', Number(value))} />
        </FieldRow>
      </SectionCard>

      {section.substrate_category === 'SUBSTRATE_CATEGORY_STEEL_DECK' && (
        <SectionCard title="Steel deck profile" defaultOpen>
          <FieldRow>
            <Field label="Sheet thickness" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.sheet_thickness?.millimetres)}
              onChange={(value) => setDeck('sheet_thickness', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
            <Field label="Profile depth" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.profile_depth?.millimetres)}
              onChange={(value) => setDeck('profile_depth', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
          </FieldRow>
          <FieldRow>
            <Field label="Rib pitch" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.rib_pitch?.millimetres)}
              onChange={(value) => setDeck('rib_pitch', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
            <Field label="Upper flange" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.upper_flange_width?.millimetres)}
              onChange={(value) => setDeck('upper_flange_width', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
          </FieldRow>
          <FieldRow>
            <Field label="Lower flange" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.lower_flange_width?.millimetres)}
              onChange={(value) => setDeck('lower_flange_width', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
            <Field label="Deck orientation" unit="°" type="number"
              value={numberInputValue(section.deck_profile?.orientation?.degrees)}
              onChange={(value) => setDeck('orientation', { degrees: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
          </FieldRow>
          <FieldRow>
            <Field label="Minimum edge distance" unit="mm" type="number"
              value={numberInputValue(section.deck_profile?.minimum_edge_distance?.millimetres)}
              onChange={(value) => setDeck('minimum_edge_distance', { millimetres: finiteOrZero(Number(value)), origin: 'VALUE_ORIGIN_INPUT' })} />
            <Field label="Web angle" unit="°" derived
              value={validation.data?.web_angle?.degrees === undefined ? '-' : validation.data.web_angle.degrees.toFixed(1)} />
          </FieldRow>
          <label className="checkbox-field">
            <input type="checkbox" checked={Boolean(section.deck_profile?.one_fastener_per_rib)}
              onChange={(event) => setDeck('one_fastener_per_rib', event.target.checked)} />
            <span>One fastener per rib</span>
          </label>
          {!resolveDeckPreviewGeometry(section.deck_profile) && (
            <p className="field-note">Enter the actual profile depth, rib pitch and flange widths to display the steel deck geometry. Missing manufacturer/profile dimensions are not inferred.</p>
          )}
        </SectionCard>
      )}

      <SectionCard title="Roof build-up layers" defaultOpen={false}>
        <div className="build-up-layer-editor">
          {layers.map((layer, index) => (
            <BuildUpLayerRow
              key={layer.id ?? index}
              layer={layer}
              index={index}
              count={layers.length}
              onChange={(next) => updateSection({ ...section, layers: replaceAt(layers, index, next) })}
              onMove={(direction) => updateSection({ ...section, layers: moveLayer(layers, index, direction) })}
              onDuplicate={() => updateSection({ ...section, layers: [
                ...layers.slice(0, index + 1),
                { ...layer, id: uniqueId('layer'), display_name: `${layer.display_name || 'Layer'} copy` },
                ...layers.slice(index + 1),
              ] })}
              onDelete={() => updateSection({ ...section, layers: layers.filter((_, itemIndex) => itemIndex !== index) })}
            />
          ))}
        </div>
        <button type="button" className="toolbar-button" onClick={() => updateSection({
          ...section,
          layers: [...layers, {
            id: uniqueId('layer'),
            display_name: `Layer ${layers.length + 1}`,
            thickness: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' },
            hatch_token: 'generic-layer',
          }],
        })}>Add layer</button>
      </SectionCard>

      {(validation.data?.messages?.length ?? 0) > 0 && (
        <div className="section-messages"><MessageList messages={validation.data?.messages ?? []} emptyText="" /></div>
      )}
    </div>
  );
}

/**
 * Visual verification only. Editable inputs live in Roof Module.
 */
export function ProfessionalSectionEditor({ module }: SectionProfileProps) {
  const { section, validation } = useValidatedSection(module);
  if (!module) return <div className="empty-state">Select a roof to view its section.</div>;

  return (
    <div className="section-editor professional-section-editor section-editor-visual-only">
      <div className="section-editor-drawing professional-section-drawing">
        <SectionSchematic section={validation.data?.section ?? section} />
        <div className="section-module-caption">
          <strong>{module.display_name || module.id}</strong>
          <span>Roof height {((module.roof_height_above_ground?.millimetres ?? 0) / 1000).toFixed(2)} m</span>
        </div>
        {(validation.data?.messages?.length ?? 0) > 0 && (
          <div className="section-messages"><MessageList messages={validation.data?.messages ?? []} emptyText="" /></div>
        )}
      </div>
    </div>
  );
}

function BuildUpLayerRow({ layer, index, count, onChange, onMove, onDuplicate, onDelete }: {
  layer: BuildUpLayer;
  index: number;
  count: number;
  onChange: (layer: BuildUpLayer) => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="build-up-layer-row">
      <span className="build-up-layer-index">{index + 1}</span>
      <input className="field-input" aria-label={`Layer ${index + 1} name`} value={layer.display_name ?? ''}
        onChange={(event) => onChange({ ...layer, display_name: event.target.value })} />
      <label className="build-up-layer-thickness">mm<input className="field-input" type="number" value={layer.thickness?.millimetres ?? 0}
        onChange={(event) => onChange({ ...layer, thickness: { millimetres: finiteOrZero(Number(event.target.value)), origin: 'VALUE_ORIGIN_INPUT' } })} /></label>
      <div className="build-up-layer-actions">
        <button type="button" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Move layer up">↑</button>
        <button type="button" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Move layer down">↓</button>
        <button type="button" onClick={onDuplicate}>Duplicate</button>
        <button type="button" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

interface ResolvedDeckPreviewGeometry {
  pitch: number;
  upper: number;
  lower: number;
  depth: number;
}

function SectionSchematic({ section }: { section: SectionProfile }) {
  const layers = section.layers ?? [];
  const insulation = (section.base_insulation_thickness?.millimetres ?? 0)
    + (section.additional_insulation_thickness?.millimetres ?? 0)
    + (section.existing_layer_thickness?.millimetres ?? 0);
  const isDeck = section.substrate_category === 'SUBSTRATE_CATEGORY_STEEL_DECK';
  const deckGeometry = isDeck ? resolveDeckPreviewGeometry(section.deck_profile) : undefined;
  const substrateDisplayDepth = isDeck
    ? (deckGeometry?.depth ?? 28)
    : Math.max(section.substrate_thickness?.millimetres ?? 40, 20);
  const worldHeight = Math.max(180, substrateDisplayDepth + insulation + layers.reduce((sum, layer) => sum + (layer.thickness?.millimetres ?? 0), 0) + 90);
  const width = 500;
  let y = 30;
  const layerRects = layers.map((layer, index) => {
    const height = Math.max(4, layer.thickness?.millimetres ?? 0);
    const rect = <g key={layer.id ?? index}>
      <rect x={40} y={y} width={width - 80} height={height} className={`section-layer section-layer-${index % 4}`} />
      <text x={50} y={y + Math.max(12, height / 2)}>{layer.display_name || `Layer ${index + 1}`}</text>
    </g>;
    y += height;
    return rect;
  });
  const insulationHeight = Math.max(8, insulation);
  const insulationY = y;
  y += insulationHeight;
  const substrateY = y;

  return (
    <svg className="section-svg professional-section-svg" viewBox={`0 0 ${width} ${worldHeight}`} role="img" aria-label="Selected roof module build-up section">
      {layerRects}
      <rect x={40} y={insulationY} width={width - 80} height={insulationHeight} className="section-insulation" />
      <text x={50} y={insulationY + Math.min(20, insulationHeight / 2 + 5)}>Insulation {insulation.toFixed(0)} mm</text>
      {isDeck ? (
        deckGeometry ? (
          <>
            <path d={buildDeckPreviewPath(deckGeometry, 40, substrateY, width - 80)} className="section-deck-profile" />
            <text x={50} y={substrateY + deckGeometry.depth + 18}>Steel deck · entered profile geometry</text>
          </>
        ) : (
          <>
            <line x1={40} y1={substrateY} x2={width - 40} y2={substrateY} className="section-deck-profile" />
            <text x={50} y={substrateY + 20}>Steel deck profile geometry unavailable</text>
          </>
        )
      ) : (
        <>
          <rect x={40} y={substrateY} width={width - 80} height={Math.max(10, substrateDisplayDepth)} className="section-deck" />
          <text x={50} y={substrateY + Math.min(22, substrateDisplayDepth / 2 + 5)}>{substrateLabel(section.substrate_category)}</text>
        </>
      )}
      <line x1={width - 25} y1={30} x2={width - 25} y2={substrateY + substrateDisplayDepth} className="section-dimension-line" />
      <text x={width - 32} y={(30 + substrateY + substrateDisplayDepth) / 2} textAnchor="middle" transform={`rotate(-90 ${width - 32} ${(30 + substrateY + substrateDisplayDepth) / 2})`}>
        Fixing length {wholeMillimetres(section.total_fixing_length?.millimetres)} mm
      </text>
    </svg>
  );
}

function resolveDeckPreviewGeometry(deck: SteelDeckProfileGeometry | undefined): ResolvedDeckPreviewGeometry | undefined {
  const pitch = deck?.rib_pitch?.millimetres;
  const upper = deck?.upper_flange_width?.millimetres;
  const lower = deck?.lower_flange_width?.millimetres;
  const depth = deck?.profile_depth?.millimetres;
  if (
    pitch === undefined || upper === undefined || lower === undefined || depth === undefined
    || pitch <= 0 || upper <= 0 || lower <= 0 || depth <= 0
    || upper + lower >= pitch
  ) {
    return undefined;
  }
  return { pitch, upper, lower, depth };
}

function buildDeckPreviewPath(
  deck: ResolvedDeckPreviewGeometry,
  x0: number,
  topY: number,
  availableWidth: number,
): string {
  const webRun = (deck.pitch - deck.upper - deck.lower) / 2;
  const pitches = Math.max(2, Math.floor(availableWidth / deck.pitch));
  const geometryWidth = pitches * deck.pitch;
  const scale = Math.min(1, availableWidth / geometryWidth);
  const sx = (value: number) => x0 + value * scale;
  const commands: string[] = [`M ${sx(0)} ${topY}`];

  for (let index = 0; index < pitches; index += 1) {
    const origin = index * deck.pitch;
    const upperEnd = origin + deck.upper;
    const valleyStart = upperEnd + webRun;
    const valleyEnd = valleyStart + deck.lower;
    const nextPitch = origin + deck.pitch;

    commands.push(`L ${sx(upperEnd)} ${topY}`);
    commands.push(`L ${sx(valleyStart)} ${topY + deck.depth}`);
    commands.push(`L ${sx(valleyEnd)} ${topY + deck.depth}`);
    commands.push(`L ${sx(nextPitch)} ${topY}`);
  }
  return commands.join(' ');
}

function normalizeSection(input: SectionProfile | undefined): SectionProfile {
  const category = input?.substrate_category ?? 'SUBSTRATE_CATEGORY_STEEL_DECK';
  return {
    ...input,
    substrate_category: category,
    substrate_thickness: input?.substrate_thickness,
    deck_profile: input?.deck_profile,
    layers: input?.layers?.length ? input.layers : [{ id: 'membrane', display_name: 'Membrane', thickness: { millimetres: 1.5 }, hatch_token: 'membrane' }],
    base_insulation_thickness: input?.base_insulation_thickness ?? { millimetres: 120 },
    additional_insulation_thickness: input?.additional_insulation_thickness ?? { millimetres: 0 },
    existing_layer_thickness: input?.existing_layer_thickness ?? { millimetres: 0 },
    anchorage_depth: input?.anchorage_depth ?? { millimetres: 25 },
  };
}

function replaceAt<T>(items: readonly T[], index: number, next: T): T[] {
  return items.map((item, itemIndex) => itemIndex === index ? next : item);
}

function moveLayer(items: readonly BuildUpLayer[], index: number, direction: -1 | 1): BuildUpLayer[] {
  const target = index + direction;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  const current = next[index];
  const replacement = next[target];
  if (!current || !replacement) return next;
  next[index] = replacement;
  next[target] = current;
  return next;
}

function finiteOrZero(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function numberInputValue(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value) ? '' : String(value);
}

function wholeMillimetres(value: number | undefined): string {
  return value === undefined || !Number.isFinite(value) ? '-' : Math.round(value).toLocaleString('en-GB');
}

function uniqueId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function substrateLabel(category: SubstrateCategory | undefined): string {
  return SUBSTRATES.find((item) => item.value === category)?.label ?? 'Substrate';
}
